import { Link } from "react-router-dom";
import { ArrowRight, Globe2, Building2, Compass, ShieldCheck, Phone } from "lucide-react";
import { SITE, IMG } from "@/lib/site";
import Chapter from "@/components/cinema/Chapter";
import { Reveal, LineReveal } from "@/components/cinema/Reveal";

const coverage = [
  { icon: Building2, t: "Downtown & Toronto Core", r: "Toronto", l: "Central & waterfront" },
  { icon: Globe2, t: "Peel & Halton", r: "West GTA", l: "Mississauga · Brampton · Oakville · Burlington" },
  { icon: Compass, t: "York & Durham", r: "North & East GTA", l: "Vaughan · Markham · Richmond Hill · Ajax · Pickering · Whitby · Oshawa" },
];

const values = [
  { t: "Care", d: "We treat every belonging as if it were our own. Careful documentation, careful handling, careful return." },
  { t: "Clarity", d: "Clear communication with homeowners, contractors, property managers and insurance professionals at every stage." },
  { t: "Coordination", d: "A single point of contact and a coordinated crew, so projects close cleanly and on time." },
];

export default function About() {
  return (
    <div data-testid="page-about" className="bg-[hsl(var(--gaza-teal-deep))] text-white">
      <Chapter img={IMG.crewTruck} overlay="left" testId="about-header">
        <div className="max-w-none">
          <Reveal y={16}><div className="eyebrow eyebrow-light mb-6">About BackHome Restoration Inc.</div></Reveal>
          <LineReveal as="h1" className="display-serif text-4xl sm:text-5xl md:text-[3.5rem] leading-[0.95]" lines={["A trusted contents and", "restoration partner across", "Toronto and the GTA."]} />
          <Reveal delay={0.5} className="mt-10 text-white/85 leading-relaxed text-lg max-w-2xl">
            We exist for the moments that cannot wait, and for the long, careful work that follows. From first dispatch to final pack-back, BackHome brings calm, considered execution to every project we touch.
          </Reveal>
        </div>
      </Chapter>

      <Chapter img={IMG.aboutFire} overlay="left" testId="about-narrative">
        <div className="grid md:grid-cols-12 gap-16 items-center">
          <div className="md:col-span-6">
            <Reveal y={16}><div className="eyebrow eyebrow-light mb-6">Who we are</div></Reveal>
            <LineReveal className="display-serif text-4xl md:text-6xl leading-[0.95]" lines={["Built to protect", "what matters when", "life is disrupted."]} />
            <Reveal delay={0.5}><div className="mt-8 text-xs text-white/55 italic">Fire damage. The work begins the moment the smoke clears.</div></Reveal>
          </div>
          <div className="md:col-span-6 space-y-6 text-lg leading-relaxed">
            <Reveal delay={0.3}>
              <p className="text-white font-semibold text-xl">
                BackHome Restoration Inc. provides emergency restoration support, contents assessment, inventory management, pack-out and pack-back services, storage solutions and professional moving services across Toronto and the GTA.
              </p>
            </Reveal>
            <Reveal delay={0.45}>
              <p className="text-white/75">
                Our team works closely with homeowners, restoration contractors, property managers and insurance professionals to protect personal belongings, minimize disruption and simplify the recovery process after a loss.
              </p>
            </Reveal>
            <Reveal delay={0.6}>
              <p className="text-white/75">
                Whether you are dealing with water damage, fire damage, mold contamination or a major relocation project, we have the experience, resources and professional network to help.
              </p>
            </Reveal>
          </div>
        </div>
      </Chapter>

      <Chapter img={IMG.torontoBlueHour} overlay="dark" testId="about-coverage">
        <div className="max-w-3xl mb-14">
          <Reveal y={16}><div className="eyebrow eyebrow-light mb-5">Coverage</div></Reveal>
          <LineReveal className="display-serif text-4xl md:text-6xl leading-[0.95]" lines={["Local responsiveness", "across Toronto and the", "Greater Toronto Area."]} />
          <Reveal delay={0.45}>
            <p className="text-white/80 mt-8 leading-relaxed text-lg">
              BackHome mobilizes crews and contents specialists across the GTA, from downtown Toronto out to Oshawa in the east and Burlington in the west, arriving the same day for active losses and often within the hour.
            </p>
          </Reveal>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {coverage.map((n, i) => (
            <Reveal key={n.t} delay={0.2 + i * 0.12} className="h-full">
              <div className="glass-dark h-full p-10 hover:bg-[hsl(var(--gaza-teal-deep))]/80 transition" data-testid={`coverage-${n.r.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <n.icon className="w-7 h-7 text-[hsl(var(--gaza-blue-bright))]" strokeWidth={1.6} />
                <div className="mt-6 gold-divider"></div>
                <div className="display-serif text-2xl mt-5">{n.t}</div>
                <div className="text-white/65 mt-2 text-sm">{n.r} · {n.l}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </Chapter>

      <Chapter img={IMG.warehouseContents} overlay="dark" testId="about-values">
        <div className="grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-4">
            <Reveal y={16}><div className="eyebrow eyebrow-light mb-5">How we work</div></Reveal>
            <LineReveal className="display-serif text-4xl md:text-5xl leading-[0.95]" lines={["Three principles", "guide every", "project."]} />
          </div>
          <div className="md:col-span-8 grid sm:grid-cols-3 gap-4">
            {values.map((v, i) => (
              <Reveal key={v.t} delay={0.2 + i * 0.12} className="h-full">
                <div className="glass-dark h-full p-8" data-testid={`value-${v.t.toLowerCase()}`}>
                  <ShieldCheck className="w-6 h-6 text-[hsl(var(--gaza-blue-bright))]" strokeWidth={1.6} />
                  <div className="display-serif text-3xl mt-5">{v.t}</div>
                  <p className="text-white/75 mt-3 leading-relaxed">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Chapter>

      <Chapter img={IMG.stageBackHome} overlay="center" className="chapter-compact" testId="about-cta">
        <div className="text-center flex flex-col items-center">
          <LineReveal className="display-serif text-4xl md:text-6xl max-w-3xl leading-[0.95]" lines={["Ready when you are."]} />
          <Reveal delay={0.3}><p className="text-white/80 mt-6 max-w-xl mx-auto text-lg">From emergency dispatch to long-term storage and final pack-back, our team is on call.</p></Reveal>
          <Reveal delay={0.45}>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              <a href={SITE.phoneHref} className="btn-gold" data-testid="about-call-btn"><Phone className="w-4 h-4" /> Call {SITE.phone}</a>
              <Link to="/contact" className="btn-outline" data-testid="about-contact-link">Send a message <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </Reveal>
        </div>
      </Chapter>
    </div>
  );
}
